import { ContentState } from "@whitehaven/ui";
import type {
  AdminLeadListCursor,
  AdminLeadListItem,
} from "@whitehaven/contracts";
import type { AuthorizationSubject } from "@whitehaven/permissions";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import { adminCrmAuthorizationKey } from "./admin-crm-authorization";
import { AdminCrmViewNavigation } from "./AdminCrmViewNavigation";
import {
  canViewAdminLeadList,
  defaultAdminLeadListFilters,
  isAdminLeadListPageConsistent,
  mergeAdminLeadListItems,
  type AdminLeadListFilters,
} from "./admin-lead-list-model";
import {
  ADMIN_LEAD_LIST_PAGE_SIZE,
  type AdminLeadListGateway,
} from "./lead-list-gateway";
import {
  AdminLeadListView,
  type AdminLeadListNotice,
} from "./AdminLeadListView";

interface AdminLeadListRouteProps {
  readonly subject: AuthorizationSubject | null;
  readonly gateway: AdminLeadListGateway | null;
}

type LeadListRequestMode = "initial" | "refresh" | "load-more";

type AdminLeadListGatewayState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | {
      readonly kind: "ready";
      readonly items: readonly AdminLeadListItem[];
      readonly nextCursor: AdminLeadListCursor | null;
      readonly consumedCursors: readonly AdminLeadListCursor[];
      readonly refreshing: boolean;
      readonly loadingMore: boolean;
      readonly notice: AdminLeadListNotice;
    };

function failedLeadListState(
  current: AdminLeadListGatewayState,
  mode: LeadListRequestMode,
): AdminLeadListGatewayState {
  if (mode === "initial" || current.kind !== "ready") {
    return { kind: "error" };
  }

  return {
    ...current,
    refreshing: false,
    loadingMore: false,
    notice: mode === "refresh" ? "REFRESH_FAILED" : "LOAD_MORE_FAILED",
  };
}

function AdminLeadListFrame({ children }: { readonly children: ReactNode }) {
  return (
    <section
      className="portal-page crm-lead-list-page portal-screen-only"
      aria-labelledby="portal-title"
      aria-describedby="crm-lead-list-description"
    >
      <header className="portal-page__header">
        <div>
          <p className="portal-kicker">CRM workspace</p>
          <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
            Leads
          </h1>
          <p id="crm-lead-list-description">
            Review authorized wellness inquiries by pipeline stage, source,
            assignment, and recent activity.
          </p>
        </div>
      </header>
      <AdminCrmViewNavigation current="leads" />
      <div className="portal-page__content">{children}</div>
    </section>
  );
}

function RouteState({
  kind,
  title,
  children,
  actions,
}: {
  readonly kind: "loading" | "warning" | "error";
  readonly title: string;
  readonly children: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <ContentState
      kind={kind}
      title={title}
      actions={actions}
      live={kind === "error"}
    >
      {children}
    </ContentState>
  );
}

function AuthorizedAdminLeadList({
  gateway,
}: {
  readonly gateway: AdminLeadListGateway;
}) {
  const [gatewayState, setGatewayState] = useState<AdminLeadListGatewayState>({
    kind: "loading",
  });
  const [filters, setFilters] = useState<AdminLeadListFilters>(
    defaultAdminLeadListFilters,
  );
  const requestSequence = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);

  const requestPage = useCallback(
    async (mode: LeadListRequestMode, cursor: AdminLeadListCursor | null) => {
      const requestId = requestSequence.current + 1;
      requestSequence.current = requestId;
      activeRequest.current?.abort();

      const controller = new AbortController();
      activeRequest.current = controller;
      const protectedStateGeneration = captureProtectedStateGeneration();

      setGatewayState((current) => {
        if (mode === "initial" || current.kind !== "ready") {
          return { kind: "loading" };
        }

        return {
          ...current,
          refreshing: mode === "refresh",
          loadingMore: mode === "load-more",
          notice: null,
        };
      });

      try {
        const page = await gateway(
          { cursor, limit: ADMIN_LEAD_LIST_PAGE_SIZE },
          { signal: controller.signal },
        );

        if (
          controller.signal.aborted ||
          requestSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }

        if (!isAdminLeadListPageConsistent(page)) {
          setGatewayState((current) => failedLeadListState(current, mode));
          return;
        }

        setGatewayState((current) => {
          if (
            mode === "load-more" &&
            (cursor === null ||
              current.kind !== "ready" ||
              page.nextCursor === cursor ||
              (page.nextCursor !== null &&
                current.consumedCursors.includes(page.nextCursor)) ||
              page.items.some((item) =>
                current.items.some(
                  (currentItem) => currentItem.leadId === item.leadId,
                ),
              ))
          ) {
            return failedLeadListState(current, mode);
          }

          const items =
            mode === "load-more" && current.kind === "ready"
              ? mergeAdminLeadListItems(current.items, page.items)
              : page.items;

          return {
            kind: "ready",
            items,
            nextCursor: page.nextCursor,
            consumedCursors:
              mode === "load-more" &&
              current.kind === "ready" &&
              cursor !== null
                ? [...current.consumedCursors, cursor]
                : [],
            refreshing: false,
            loadingMore: false,
            notice: null,
          };
        });
      } catch {
        if (
          controller.signal.aborted ||
          requestSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }

        setGatewayState((current) => failedLeadListState(current, mode));
      }
    },
    [gateway],
  );

  useEffect(() => {
    const unregisterCleaner = registerProtectedStateCleaner(
      "crm-admin-lead-list",
      () => {
        requestSequence.current += 1;
        activeRequest.current?.abort();
        activeRequest.current = null;
        setGatewayState({ kind: "loading" });
        setFilters(defaultAdminLeadListFilters);
      },
    );

    void requestPage("initial", null);

    return () => {
      unregisterCleaner();
      requestSequence.current += 1;
      activeRequest.current?.abort();
      activeRequest.current = null;
    };
  }, [requestPage]);

  if (gatewayState.kind === "loading") {
    return (
      <RouteState kind="loading" title="Loading authorized leads.">
        <p>The secure lead list is being prepared.</p>
      </RouteState>
    );
  }

  if (gatewayState.kind === "error") {
    return (
      <RouteState
        kind="error"
        title="The lead list could not be loaded."
        actions={
          <button
            type="button"
            onClick={() => void requestPage("initial", null)}
          >
            Try again
          </button>
        }
      >
        <p>
          No lead details are shown. Try the authorized read again or return to
          portal home.
        </p>
      </RouteState>
    );
  }

  return (
    <AdminLeadListView
      items={gatewayState.items}
      nextCursor={gatewayState.nextCursor}
      filters={filters}
      refreshing={gatewayState.refreshing}
      loadingMore={gatewayState.loadingMore}
      notice={gatewayState.notice}
      onFiltersChange={setFilters}
      onRefresh={() => void requestPage("refresh", null)}
      onLoadMore={() => {
        if (gatewayState.nextCursor) {
          void requestPage("load-more", gatewayState.nextCursor);
        }
      }}
    />
  );
}

export function AdminLeadListRoute({
  subject,
  gateway,
}: AdminLeadListRouteProps) {
  if (!subject) {
    return (
      <AdminLeadListFrame>
        <RouteState
          kind="warning"
          title="Secure sign-in is required."
          actions={<a href="/">Return to portal home</a>}
        >
          <p>
            Lead information stays unavailable until a verified portal session
            supplies an authorized account.
          </p>
        </RouteState>
      </AdminLeadListFrame>
    );
  }

  if (!canViewAdminLeadList(subject)) {
    return (
      <AdminLeadListFrame>
        <RouteState
          kind="warning"
          title="This account cannot open the lead list."
          actions={<a href="/">Return to portal home</a>}
        >
          <p>
            No lead information was requested or displayed. Use an approved
            account with CRM access.
          </p>
        </RouteState>
      </AdminLeadListFrame>
    );
  }

  if (!gateway) {
    return (
      <AdminLeadListFrame>
        <RouteState
          kind="warning"
          title="The secure lead connection is not available yet."
          actions={<a href="/">Return to portal home</a>}
        >
          <p>
            The permission gate is ready, but the authorized CRM read service
            has not been connected. No sample or cached leads are shown.
          </p>
        </RouteState>
      </AdminLeadListFrame>
    );
  }

  return (
    <AdminLeadListFrame>
      <AuthorizedAdminLeadList
        key={adminCrmAuthorizationKey(subject)}
        gateway={gateway}
      />
    </AdminLeadListFrame>
  );
}
